/**
 * The one place that talks to the backend.
 *
 * `credentials: 'include'` on every request, because the session is a cookie
 * and the browser will not send it otherwise. Money is carried as integer
 * paise and formatted only at the edge — see `formatInr`.
 */

const BASE = '/api/v1'

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly detail?: unknown,
  ) {
    super(message)
  }

  /** 401 means "not signed in", which the app answers by showing login. */
  get isUnauthenticated() {
    return this.status === 401
  }

  /** 403 means signed in, but this role may not do that. A different message. */
  get isForbidden() {
    return this.status === 403
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`${BASE}${path}`, {
    ...init,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(init?.headers ?? {}),
    },
  })

  if (response.status === 204) return undefined as T

  const text = await response.text()
  const body = text ? JSON.parse(text) : null

  if (!response.ok) {
    // FastAPI returns `detail` as a string for our own errors and as a list of
    // objects for schema validation failures. Both are shown to a person, so
    // both need to read as sentences.
    const detail = body?.detail
    const message = Array.isArray(detail)
      ? detail.map((d: { msg?: string }) => d.msg ?? 'invalid value').join('; ')
      : typeof detail === 'string'
        ? detail
        : `request failed (${response.status})`
    throw new ApiError(response.status, message, detail)
  }
  return body as T
}

export const api = {
  get: <T,>(path: string) => request<T>(path),
  post: <T,>(path: string, body?: unknown) =>
    request<T>(path, { method: 'POST', body: JSON.stringify(body ?? {}) }),
  put: <T,>(path: string, body?: unknown) =>
    request<T>(path, { method: 'PUT', body: JSON.stringify(body ?? {}) }),
  patch: <T,>(path: string, body?: unknown) =>
    request<T>(path, { method: 'PATCH', body: JSON.stringify(body ?? {}) }),
  del: <T,>(path: string) => request<T>(path, { method: 'DELETE' }),
}

// ---------------------------------------------------------------------------
// Money
// ---------------------------------------------------------------------------

/**
 * Format integer paise the Indian way: ₹1,23,456.78.
 *
 * The backend never sends a float and never sends a formatted string; it sends
 * paise, and this is the only place that turns them into something to read.
 */
export function formatInr(paise: number): string {
  const negative = paise < 0
  const abs = Math.abs(paise)
  const rupees = Math.floor(abs / 100).toString()
  const fraction = (abs % 100).toString().padStart(2, '0')

  let grouped = rupees
  if (rupees.length > 3) {
    const tail = rupees.slice(-3)
    let head = rupees.slice(0, -3)
    const groups: string[] = []
    while (head.length > 2) {
      groups.unshift(head.slice(-2))
      head = head.slice(0, -2)
    }
    if (head) groups.unshift(head)
    grouped = [...groups, tail].join(',')
  }
  return `${negative ? '-' : ''}₹${grouped}.${fraction}`
}

// ---------------------------------------------------------------------------
// Shapes, mirroring the backend's response models
// ---------------------------------------------------------------------------

export interface Me {
  user_id: string
  email: string
  display_name: string
  role: string
  permissions: string[]
  visibility: string
  scope_location_ids: string[]
  /**
   * Their password was set by whoever created the account. While this is true
   * the backend refuses every permission-gated route, so the app shows one
   * screen — see `App.tsx`. Believing this field is not the control; the
   * control is that the requests would fail anyway.
   */
  must_change_password: boolean
}

export interface Location {
  id: string
  code: string
  name: string
  kind: string
  gstin: string | null
  state_code: string | null
  city: string | null
  is_active: boolean
  sort_order: number
}

export interface Department {
  id: string
  code: string
  name: string
  is_active: boolean
  sort_order: number
  /** The head on the organisation chart. Informational. */
  head_user_id: string | null
  /** Who approves for the department today. Null means requests needing a
   *  department step cannot be submitted. */
  authority_user_id: string | null
  authority_name: string | null
  authority_since: string | null
}

/** One dated assignment of a department's approval authority. */
export interface Authority {
  id: string
  department_id: string
  user_id: string
  user_name: string
  effective_from: string
  effective_to: string | null
  reason: string
  ended_reason: string | null
  configured_by_name: string | null
  is_current: boolean
}

export interface User {
  id: string
  email: string
  display_name: string
  role: string
  visibility: string
  scope_location_ids: string[]
  home_location_id: string | null
  department_id: string | null
  is_active: boolean
  /**
   * Still on the password whoever created the account chose, so that person
   * can currently sign in as them. Worth showing in the list: it is the one
   * state where two people hold one set of credentials.
   */
  must_change_password: boolean
}

/** What a bank line is *for*. Deliberately not the ledger account: people
 *  think in categories, the ledger thinks in accounts, and the mapping between
 *  them is configuration that can change without recategorising history. */
export interface BankCategory {
  id: string
  code: string
  name: string
  direction: string | null
  gl_account_id: string | null
  is_active: boolean
  requires_payment_request: boolean
}

export interface Account {
  id: string
  code: string
  name: string
  account_type: string
  parent_id: string | null
  path: string
  depth: number
  is_postable: boolean
  is_active: boolean
  schedule_iii_head: string | null
  /** Which section of the cash flow this account's movement belongs in. Null
   *  is a real state: an untagged account that moved is named on the cash-flow
   *  statement as the reason it does not tie. */
  cash_flow_class: string | null
  requires_location: boolean
  requires_department: boolean
  requires_party: boolean
}

export interface RoleMapping {
  system_role: string
  gl_account_id: string
  account_code: string
  account_name: string
  effective_from: string
  effective_to: string | null
}

export interface Period {
  period_label: string
  period_start: string
  period_end: string
  status: string
  reopened_count: number
}

export interface Check {
  key: string
  title: string
  severity: 'blocking' | 'advisory'
  passed: boolean
  detail: string
  count: number
}

export interface Checklist {
  period_label: string
  status: string
  ready_to_close: boolean
  checks: Check[]
}

export interface BalanceRow {
  account_id: string
  code: string
  name: string
  account_type: string
  debit_paise: number
  credit_paise: number
  signed_paise: number
}

export interface TrialBalance {
  as_at: string
  rows: BalanceRow[]
  total_debit_paise: number
  total_credit_paise: number
  difference_paise: number
  balanced: boolean
}

export interface ProfitAndLoss {
  fy_code: string
  since: string
  upto: string
  income: BalanceRow[]
  expenses: BalanceRow[]
  total_income_paise: number
  total_expense_paise: number
  profit_paise: number
}

export interface BalanceSheet {
  as_at: string
  assets: BalanceRow[]
  liabilities: BalanceRow[]
  equity: BalanceRow[]
  retained_earnings_paise: number
  total_assets_paise: number
  total_liabilities_paise: number
  total_equity_paise: number
  difference_paise: number
  balanced: boolean
}

export interface AuditRow {
  seq: number
  at: string
  actor_user_id: string | null
  actor_role: string | null
  action: string
  object_type: string
  object_id: string | null
  detail: Record<string, unknown>
  ip: string | null
  row_hash: string
}

export interface ChainHead {
  head_seq: number
  head_hash: string
  anchored_at: string | null
  anchored_seq: number | null
  rows_since_anchor: number
}

export interface QueueStep {
  step_no: number
  label: string
  kind: 'department_authority' | 'permission' | 'named_user'
  required: number
  approvals: number
  stale_approvals: number
  satisfied: boolean
  current: boolean
  assigned_user_id: string | null
  assigned_name: string | null
  note: string | null
  rerouted_from_name: string | null
  reroute_reason: string | null
}

export interface QueueDecision {
  step_no: number
  decision: string
  decided_by: string
  on_behalf_of: string | null
  at: string
  reason: string | null
}

/** One pending request, as the approval queue shows it. */
export interface QueueItem {
  request_id: string
  doc_kind: string
  doc_id: string
  reference: string
  summary: string
  /** What the document says, for somebody deciding on it. */
  facts: { label: string; value: string }[]
  amount_paise: number
  maker: string
  department: string | null
  submitted_at: string
  sla_due_at: string | null
  overdue: boolean
  state: string
  steps: QueueStep[]
  decisions: QueueDecision[]
  can_decide: boolean
  blocked_reason: string | null
  i_prepared_this: boolean
  break_glass_available: boolean
  break_glass_ceiling_paise: number
}

export interface JournalLine {
  line_no: number
  gl_account_id: string
  debit_paise: number
  credit_paise: number
  location_id: string | null
  department_id: string | null
  memo: string | null
}

export interface Journal {
  id: string
  reference: string
  journal_date: string
  narration: string
  status: string
  version: number
  amount_paise: number
  lines: JournalLine[]
  journal_entry_id: string | null
  approval_state: string
  approvals_required: number
  approvals_valid: number
  approvals_stale: number
}

export interface PolicyStep {
  step_no: number
  kind: 'department_authority' | 'permission' | 'named_user'
  label: string
  permission: string | null
  named_user_id: string | null
  named_user_name: string | null
  required_count: number
}

export interface Policy {
  id: string
  doc_kind: string
  min_amount_paise: number
  max_amount_paise: number | null
  required_approvals: number
  requires_document: boolean
  sla_days: number
  effective_from: string
  effective_to: string | null
  note: string | null
  status: 'draft' | 'pending_approval' | 'active' | 'rejected' | 'superseded'
  version: number
  location_id: string | null
  department_id: string | null
  department_name: string | null
  combined_step_ceiling_paise: number | null
  min_quotations: number
  created_by_name: string | null
  steps: PolicyStep[]
  approval_state: string
}

export const DOC_KIND_LABELS: Record<string, string> = {
  manual_journal: 'Manual journal',
  purchase_request: 'Purchase request',
  purchase_order: 'Purchase order',
  payment: 'Payment run',
  vendor_invoice: 'Vendor bill',
  customer_invoice: 'Customer invoice',
  vendor: 'Vendor',
  vendor_bank_change: 'Vendor bank change',
  expense_claim: 'Expense claim',
  payment_request: 'Payment request',
  quotation_exception: 'Quotation exception',
  credit_note: 'Credit note',
  write_off: 'Write-off',
  period_close: 'Period close',
  role_assignment: 'Role assignment',
  approval_rule: 'Approval rule',
  tax_return: 'Tax return',
  commitment: 'Commitment',
  commitment_revision: 'Commitment revision',
  financing_instrument: 'Loan',
  financing_event: 'Change to a loan',
  capital_contribution: 'Capital contribution',
  share_allotment: 'Share allotment',
}
